import { useMemo } from 'react';
import useMarketSelectorSpotMarkets from 'src/components/MarketSelector/hooks/useMarketSelectorSpotMarkets';
import useMarketSelectorPerpMarkets from 'src/components/MarketSelector/hooks/useMarketSelectorPerpMarkets';
import {
	perpMarketCategoryFilter,
	spotMarketCategoryFilter,
} from 'src/components/MarketSelector/utils';
import { MarketCategory, MARKET_TAGS } from 'src/constants/markets';
import { getTargetPerpMarketAccount } from 'src/hooks/useTargetMarketAccount';
import {
	PerpMarketConfig,
	SpotMarketConfig,
	BigNum,
	PRICE_PRECISION_EXP,
} from '@drift-labs/sdk';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import useDriftClientIsReady from 'src/hooks/useDriftClientIsReady';
import { MarketSelectorSort } from 'src/components/MarketSelector/types';

interface Params {
	selectedCategory: MarketCategory;
	searchValue: string;
	sort: MarketSelectorSort | undefined;
	isFavoritesSticky: boolean;
}

export default function useMarketSelectorDisplayedMarkets({
	selectedCategory,
	searchValue,
	sort,
	isFavoritesSticky,
}: Params) {
	const driftClientIsReady = useDriftClientIsReady();
	const driftClient = useDriftStore((s) => s.driftClient.client);

	const defaultSortedPerpMarkets = useMarketSelectorPerpMarkets();
	const defaultSortedSpotMarkets = useMarketSelectorSpotMarkets();

	return useMemo(() => {
		const isAllMarkets = selectedCategory === 'all';
		const isSpotMarkets = selectedCategory === 'spot';
		const allMarkets = [
			...defaultSortedPerpMarkets,
			...defaultSortedSpotMarkets,
		];

		const categoryFilteredMarkets = (() => {
			if (isAllMarkets) {
				return allMarkets;
			}

			if (isSpotMarkets) {
				return [...defaultSortedSpotMarkets];
			}

			return allMarkets.filter((market) => {
				const perpMarketAccount =
					driftClient && driftClientIsReady
						? getTargetPerpMarketAccount(
								market.market.market.marketIndex,
								driftClient
							)
						: undefined;

				return perpMarketAccount
					? perpMarketCategoryFilter(
							market.market.market as PerpMarketConfig,
							selectedCategory,
							market.isHot ?? false,
							perpMarketAccount
						)
					: spotMarketCategoryFilter(
							market.market.market as SpotMarketConfig,
							selectedCategory
						);
			});
		})();

		const searchFilteredMarkets = (() => {
			if (!searchValue) {
				return categoryFilteredMarkets;
			}

			return categoryFilteredMarkets.filter((market) => {
				let tags = [
					market.symbol.toLowerCase(),
					market.baseAssetSymbol.toLowerCase(),
				];

				const isPerpMarket = market.market.isPerp;

				tags = tags.concat(
					(isPerpMarket
						? MARKET_TAGS.perp[
								market.market.marketIndex as keyof typeof MARKET_TAGS.perp
							]
						: MARKET_TAGS.spot[
								market.market.marketIndex as keyof typeof MARKET_TAGS.spot
							]) ?? []
				);

				if (isPerpMarket) {
					tags.push(
						(
							market.market.market as PerpMarketConfig
						)?.fullName?.toLowerCase() ?? ''
					);
				}

				return tags.some((tag) => tag.includes(searchValue.toLowerCase()));
			});
		})();

		const sortedMarkets = searchFilteredMarkets; // naturally split between perp and spot already

		if (sort) {
			const { header, isDescending } = sort;
			sortedMarkets.sort((a, b) => {
				if (isFavoritesSticky && a.isFavorite !== b.isFavorite) {
					return a.isFavorite ? -1 : 1;
				}

				if (header === 'market') {
					return isDescending
						? b.symbol.localeCompare(a.symbol)
						: a.symbol.localeCompare(b.symbol);
				}

				if (header === 'price') {
					return isDescending
						? (b.price
								?.sub(a?.price ?? BigNum.zero(PRICE_PRECISION_EXP))
								.toNum() ?? 0)
						: (a.price
								?.sub(b?.price ?? BigNum.zero(PRICE_PRECISION_EXP))
								.toNum() ?? 0);
				}

				if (header === 'priceChangePct') {
					return isDescending
						? (b?.pricePercentChange ?? 0) - (a?.pricePercentChange ?? 0)
						: (a?.pricePercentChange ?? 0) - (b?.pricePercentChange ?? 0);
				}

				if (header === 'volume') {
					return isDescending
						? (b?.volume ?? 0) - (a?.volume ?? 0)
						: (a?.volume ?? 0) - (b?.volume ?? 0);
				}

				if (header === 'openInterest') {
					return isDescending
						? (b?.openInterest ?? 0) - (a?.openInterest ?? 0)
						: (a?.openInterest ?? 0) - (b?.openInterest ?? 0);
				}

				if (header === 'funding') {
					return isDescending
						? (b?.funding24H ?? 0) - (a?.funding24H ?? 0)
						: (a?.funding24H ?? 0) - (b?.funding24H ?? 0);
				}

				if (header === 'priceLow24h') {
					return isDescending
						? (b?.low ?? 0) - (a?.low ?? 0)
						: (a?.low ?? 0) - (b?.low ?? 0);
				}

				if (header === 'priceHigh24h') {
					return isDescending
						? (b?.high ?? 0) - (a?.high ?? 0)
						: (a?.high ?? 0) - (b?.high ?? 0);
				}

				return 0;
			});
		}

		return sortedMarkets;
	}, [
		selectedCategory,
		defaultSortedPerpMarkets,
		defaultSortedSpotMarkets,
		searchValue,
		sort,
		driftClient,
		driftClientIsReady,
		isFavoritesSticky,
	]);
}
